import axios from '../axios';
import * as cookies from '../cookies';
import { HandleRequestError } from '../errors';
import * as models from '../models';
import { IsObject } from '../object';
import { GetPageRequestID } from './pagerequests';
import { Session } from './session';

declare interface myWindow extends Window {
    dataLayer: Record<string, unknown>[];
}
declare const window: myWindow;

export declare interface CreateCartItemResp {
    cartID: string;
    cartItemID: string[];
    cartItems: models.CartItem[];
    cartItemsCount: number;
}

export declare interface AddCartItemParamsQuote {
    quoteID: string;
}

export declare interface AddCartItemParamsOrder {
    orderID: string;
}

export declare interface AddCartItemParamsStockItem {
    stockItemID: string;
    quantity: number;
    customizations?: Record<string, unknown>;
    cartItemUploadIDs?: number[];
}

export declare interface AddCartItemParamsUpload {
    uploadID: number;
    quantity: number;
    options?: AddCartItemOption[];
    cartItemUploadIDs?: number[];
    cartItemUploads?:
    {
        uploadID: number;
        tags: string[];
    }[];
    uploadLineItem: {
        name?: string;
        lineItem: string;
        type: string;
        size: string;
        childLineItems: {
            child: string;
            option: string;
        }[];
    };
}

export declare interface AddCartItemParamsCart {
    cartID: string;
}

export declare type AddCartItemParams =
    AddCartItemParamsQuote |
    AddCartItemParamsOrder |
    AddCartItemParamsStockItem |
    AddCartItemParamsUpload |
    AddCartItemParamsCart;

export declare interface AddCartItemOption {
    name: string;
    value: string;
}

let cartID: string | undefined;

export function GetCartID(): string {
    if (!cartID) {
        cartID = window.sessionStorage.getItem('cartID') || window.localStorage.getItem('cartID') || cookies.Get('cartID') || '';
    }
    return cartID;
}

export async function AddCartItems(reqCartItems: AddCartItemParams | AddCartItemParams[], newCart: boolean = false) {
    const cartItems: AddCartItemParams[] = [];
    if (Array.isArray(reqCartItems)) {
        cartItems.push(...reqCartItems);
    } else {
        cartItems.push(reqCartItems);
    }

    const sess = await Session().catch(() => undefined);

    const dataLayer = window.dataLayer || [];
    dataLayer.push({
        'event': 'added-to-cart',
        'items': reqCartItems,
        'userData': {
            'email': sess?.email || '',
            'phone': sess?.phone || '',
            'company': sess?.company || '',
            'firstName': sess?.firstName || '',
            'lastName': sess?.lastName || '',
        },
        'origin': window.location.href,
    });

    if (newCart) {
        await ForgetCart();
    }

    return axios.post<CreateCartItemResp>('/cart-items', {
        'cartID': !newCart ? GetCartID() : undefined,
        'cartItems': cartItems,
        'pageRequestID': await GetPageRequestID(),
    }).then(resp => {
        SetCartID(resp.data.cartID);
        return resp.data;
    }).catch(err => {
        HandleRequestError(err);
        throw err;
    });
}

export function SetCartID(cartID: string) {
    window.localStorage.setItem('cartID', cartID);
    window.sessionStorage.setItem('cartID', cartID);
}

export function RestoreCartItem(cartItemID: string) {
    return axios.patch(`/cart-items/${cartItemID}`, { 'active': true });
}

export async function ForgetCart() {
    GetCartID();

    if (cartID && cartID === window.localStorage.getItem('cartID')) {
        window.localStorage.removeItem('cartID');
    }
    window.sessionStorage.removeItem('cartID');
    await axios.delete('/carts/cookie');
    cartID = undefined;
}

export async function DeleteCart() {
    const cartID = GetCartID();
    await ForgetCart();
    return axios.delete(`/carts/${cartID}`);
}

export declare interface CartResponse {
    cart: models.Cart;
    subtotal: number;
    discount: number;
    shipping: number;
    taxAmount: number;
    taxRate: number;
    total: number;
}

export declare interface TaxAddress {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    stateID?: number;
    zip: string;
    countryID: number;
}

declare interface cartReq extends Partial<TaxAddress> {
    promo?: string;
}

export async function GetCart(shippingAddress?: TaxAddress, promo?: string): Promise<CartResponse | undefined> {
    const req: cartReq = shippingAddress || {};
    if (promo) {
        req.promo = promo;
    }

    return axios.get<CartResponse>(`/v2/carts/${GetCartID()}`, {
        params: req,
    }).then(resp => resp.data).
        catch(err => {
            if (IsObject(err) && IsObject(err['response']) && err['response']['status'] === 404) {
                void ForgetCart();
                return undefined;
            }
            throw err;
        });
}

declare interface cartCountResp {
    count: number;
}

export async function GetCartCount(): Promise<number> {
    if (!GetCartID()) {
        return 0;
    }

    return axios.get<cartCountResp>(`/v2/carts/${GetCartID()}/count`).
        then(resp => resp.data.count).
        catch(err => {
            if (IsObject(err) && IsObject(err['response']) && err['response']['status'] === 404) {
                void ForgetCart();
                return 0;
            }
            throw err;
        });
}

function cartsEqual(cart1?: models.Cart, cart2?: models.Cart): boolean {
    return JSON.stringify(cart1) === JSON.stringify(cart2);
}

export function CartAutoReload(): AbortController {
    void GetCart().then(initialCart => {
        setInterval(async () => {
            if (controller.signal.aborted) {
                return;
            }

            const currCart = await GetCart();
            // run it again since we're not in a different "block"
            if (controller.signal.aborted) {
                return;
            }

            if (!cartsEqual(initialCart?.cart, currCart?.cart)) {
                window.location.reload();
            }
        }, 5000);
    });

    const controller = new AbortController();
    return controller;
}